<template>
  <div class="flex flex-col h-full overflow-hidden bg-white">
    <BaseTable
      v-if="filteredDocumentTypeSeries"
      table-id="documentSeriesTypes"
      :headers="tableHeaders"
      :list="filteredDocumentTypeSeries"
      :is-add-new-hidden="mode != 'edit'"
      :is-search-hidden="true"
      clickable
      hide-grouping
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="refreshData"
      @table-item-clicked="onItemClick">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="isFromManager && item.isFromManager && !item.deleted && mode == 'edit'"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as DocumentSeriesType)" />
        </div>
      </template>
    </BaseTable>

    <BaseModalEditCreate
      v-if="isModalActive"
      :mode="modalMode"
      :is-modified="isModalModified"
      @save="modalSave"
      @dismiss-changes="onDiscardModalChanges">
      <template #form>
        <DocumentSeriesTypeCreateEdit
          v-model="modifiedDocumentSeriesType"
          :document-series-error-message="
            $vDocSeries.series.required.$invalid ? $vDocSeries.series.required.$message : ''
          " />
      </template>
    </BaseModalEditCreate>

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';

import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import DocumentSeriesTypeCreateEdit from '@/components/documents/DocumentSeriesTypeCreateEdit.vue';

import type { DocumentSeriesType } from '@/types/DocumentSeriesTypes';
import type { DocumentType } from '@/types/DocumentTypes';
import { RecordMode, type TableFilterData } from '@/types/Base';
import { sortData } from '@/utils/table';
import { deepCopy } from '@/utils/data';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  isFromManager: boolean;
}>();

const data = defineModel<DocumentType>();
const toastStore = useToastStore();
const { t: $t } = useI18n();
const { documentSeries } = storeToRefs(useLookupsStore());

const tableHeaders = [
  {
    value: 'documentSeries.name',
    text: $t('document-type.documentSeries'),
    type: 'text',
    active: true,
  },
  {
    value: 'defaultWarehouse.name',
    text: $t('document-type.defaultWarehouse'),
    type: 'text',
    active: true,
  },
  { value: 'sequenceFormat', text: $t('document-type.sequenceFormat'), type: 'text', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const documentSeriesType = ref<DocumentSeriesType>();
const modifiedDocumentSeriesType = ref<DocumentSeriesType>();
const isModalActive = ref(false);
const modalMode = ref<'add' | 'edit'>('add');
const isModalModified = ref<boolean>(false);
const modalDeleteRow = ref(false);
const deleteRowItem = ref<DocumentSeriesType>();

const filteredDocumentTypeSeries = computed(() => {
  return data.value?.documentSeriesType?.filter((x) => x.recordMode != 3);
});

const validationDocumentSeriesModel = ref({
  series: modifiedDocumentSeriesType.value?.documentSeriesID || '',
});

const rulesDocSeries = computed(() => ({
  series: modifiedDocumentSeriesType.value?.recordMode
    ? { required: helpers.withMessage($t('messages.errors.requiredField'), required) }
    : {},
}));

const $vDocSeries = useVuelidate(rulesDocSeries, validationDocumentSeriesModel, { $lazy: true });

watch(
  () => modifiedDocumentSeriesType.value,
  (newData) => {
    isModalModified.value = JSON.stringify(newData) !== JSON.stringify(documentSeriesType.value);

    validationDocumentSeriesModel.value.series = newData?.documentSeriesID || '';
    $vDocSeries.value.$touch();
  },
  { deep: true }
);

async function refreshData(tableData: TableFilterData) {
  if (!data.value?.documentSeriesType) return;

  if (tableData.column !== undefined && tableData.order) {
    data.value.documentSeriesType = sortData(
      data.value.documentSeriesType,
      tableData.column,
      tableData.order,
      tableHeaders
    );
  }
}

function onAddNew() {
  if (props.isFromManager && props.mode == 'edit') {
    documentSeriesType.value = initializeDocumentSeriesType();
    if (documentSeriesType.value) {
      modifiedDocumentSeriesType.value = deepCopy<DocumentSeriesType>(documentSeriesType.value);
    }
    isModalActive.value = true;
    modalMode.value = 'add';
  }
}

function initializeDocumentSeriesType(): DocumentSeriesType | undefined {
  if (data.value) {
    return {
      id: uuidv4(),
      documentTypeID: data.value.id,
      documentSeriesID: '',
      documentType: {
        id: data.value.id,
        code: data.value.code || '',
        name: data.value.name || '',
      },
      documentSeries: {
        id: '',
        code: '',
        name: '',
      },
      isDefault: 0,
      isActive: true,
      isFromManager: true,
      deleted: false,
      recordMode: 1,
    };
  }
}

function onDeleteRow(item: DocumentSeriesType) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!data.value || !deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;

  if (!Array.isArray(data.value.documentSeriesType)) data.value.documentSeriesType = [];

  const indexDocumentType = data.value.documentSeriesType?.findIndex(
    (existingItem) => existingItem.id === idToDelete
  );
  if (indexDocumentType !== -1 && indexDocumentType !== undefined) {
    if (deleteRowItem.value.recordMode === RecordMode.Added)
      data.value.documentSeriesType.splice(indexDocumentType, 1);
    else {
      deleteRowItem.value.recordMode = RecordMode.Deleted;
      data.value.documentSeriesType[indexDocumentType] = deleteRowItem.value;
    }
  }
}

async function onDeleteMultiselect(items: DocumentSeriesType[]) {
  if (props.isFromManager) {
    const itemsArray = Array.isArray(items) ? items : [items];
    const itemsToDelete = itemsArray.filter((item) => item.isFromManager);
    if (itemsToDelete.length === 0) {
      toastStore.error({ text: 'Selected items cannot be deleted.' });
      return;
    }

    itemsToDelete.forEach(async (item) => {
      deleteRowItem.value = item;
      await onDeleteSingle();
    });
  }
}

async function modalSave() {
  $vDocSeries.value.$touch();
  if ($vDocSeries.value.$invalid) return;

  if (!data.value || !modifiedDocumentSeriesType.value) return;

  if (!Array.isArray(data.value.documentSeriesType)) data.value.documentSeriesType = [];

  const docSeries = documentSeries.value.find(
    (item) => item.id === modifiedDocumentSeriesType.value?.documentSeriesID
  );
  if (docSeries) {
    modifiedDocumentSeriesType.value.documentSeries = {
      id: docSeries.id,
      code: docSeries.code || '',
      name: docSeries.name || '',
    };
  }

  const indexDocumentType = data.value.documentSeriesType.findIndex(
    (existingItem) => existingItem.id === modifiedDocumentSeriesType.value?.id
  );
  if (indexDocumentType !== -1 && indexDocumentType !== undefined)
    data.value.documentSeriesType[indexDocumentType] = modifiedDocumentSeriesType.value;
  else data.value.documentSeriesType.push(modifiedDocumentSeriesType.value);

  documentSeriesType.value = deepCopy<DocumentSeriesType>(modifiedDocumentSeriesType.value);
  isModalActive.value = false;
  modalMode.value = 'add';
}

function onDiscardModalChanges() {
  if (!documentSeriesType.value) return;
  modifiedDocumentSeriesType.value = deepCopy<DocumentSeriesType>(documentSeriesType.value);
  isModalActive.value = false;
  modalMode.value = 'add';
}

function onItemClick(item: DocumentSeriesType) {
  if (props.isFromManager && item.isFromManager && props.mode == 'edit') {
    documentSeriesType.value = item;
    if (documentSeriesType.value.recordMode !== RecordMode.Added)
      documentSeriesType.value.recordMode = RecordMode.Modified;
    modifiedDocumentSeriesType.value = deepCopy<DocumentSeriesType>(documentSeriesType.value);
    isModalActive.value = true;
    modalMode.value = 'edit';
  }
}
</script>
